import type { Journey as JourneyV3 } from '../../..'
import * as next from '../../../types/next'
import {
  isContextLogicCondition,
  logicActionTypes,
  logicOperators,
  LogicTriggerEventName,
  parseLogicConditionSource,
  parseLogicConditionValue,
  parseLogicConditionValueFromInternalIntegration,
  parseLogicConditionValueFromPath,
  parseLogicLiveValueFromPath
} from '../../../utils'
import type {
  LogicActionTypesValues,
  LogicCondition,
  LogicOperatorsValues,
  LogicAction
} from '../../../utils'
import { uncastObjectSchema } from '../generated'
import type { UpLogicHandler } from '../types'
import {
  findBlockIdByLogicCondition,
  MigrationStore,
  newId,
  parseBlockPath
} from '../utils'

import { up as upDisplayBlock } from './displayCondition'
import { up as upJumpToStep } from './jumpToStep'
import { up as upSkipStep } from './skipStep'

const upLogicMap: Record<LogicActionTypesValues, UpLogicHandler> = {
  [logicActionTypes.NavigateToStep]: upJumpToStep,
  [logicActionTypes.SkipStep]: upSkipStep,
  [logicActionTypes.DisplayBlock]: upDisplayBlock
}

export const upLogicControl = (
  condition: LogicCondition,
  action: LogicAction,
  sourceJourney: JourneyV3
): Omit<next.Logic, 'id' | 'orderIndex'> | undefined => {
  // Validate action and condition objects
  if (!condition) {
    throw new Error(`Condition cannot be undefined`)
  }

  if (!action) {
    throw new Error(`Action cannot be undefined`)
  }

  if (!condition.conditionSource) {
    throw new Error(`Condition source is required`)
  }

  // upTrigger
  const trigger =
    condition.event === LogicTriggerEventName.NEXT
      ? next.LOGIC_TRIGGER.NEXT_CLICKED
      : next.LOGIC_TRIGGER.VALUE_CHANGED

  // Get the upgrade function for action type
  const up = upLogicMap[action.actionType as next.LOGIC_ACTION]

  if (typeof up !== 'function') {
    if (action.actionType === logicActionTypes.InjectFilter) {
      // InjectFilter will not be migrated as a logic, but a reference in product block. Skipping it
      return
    }
    throw new Error(
      `ImplementationError: Logic cannot be upgraded because up for ${action.actionType} is not a function or does not exist`
    )
  }

  // Get block ID for source block
  const sourceBlockId = findBlockIdByLogicCondition(sourceJourney, condition)

  if (sourceBlockId) {
    // Parse source block path
    const blockPath = parseBlockPath(
      parseLogicConditionSource(condition.conditionSource)
    )

    if (!blockPath)
      throw new Error(
        `Could not parse source block path from condition ${condition.conditionSource}`
      )

    const sourceBlock = MigrationStore.getInstance().getBlock(sourceBlockId)

    if (!sourceBlock) {
      throw new Error(
        `Could not find source block with ID ${sourceBlockId} in journey store`
      )
    }

    const migratedLogic = up(action, sourceJourney)

    if (!migratedLogic) {
      return
    }

    return {
      ...migratedLogic,
      triggeredOn: trigger,
      conditions: upConditionWithBlocks(
        action.conditionResult,
        condition,
        sourceBlockId,
        sourceBlock
      )
    }
  }

  // When logic source is a context param
  const conditionSource = parseLogicConditionSource(condition.conditionSource)

  if (
    conditionSource &&
    isContextLogicCondition(conditionSource, { isParsed: true })
  ) {
    const [_, paramKey] = conditionSource.split('/')

    if (paramKey) {
      const migratedLogic = up(action, sourceJourney)

      if (!migratedLogic) {
        // When the logic is not supported or skipped, we return undefined
        return
      }

      return {
        ...migratedLogic,
        triggeredOn: trigger,
        conditions: upConditionWithContextParams(condition, paramKey)
      }
    }
  }

  // If the logic source is not a block or context param, we skip it
  return undefined
}

const upConditionWithContextParams = (
  condition: LogicCondition,
  paramKey: string
): next.Clause[][] => {
  const operator = upOperator(condition.conditionOperator, true)
  const value = parseLogicConditionValue(condition.conditionValue)
  const clause: next.Clause = {
    operator: operator,
    fact: {
      id: newId<next.ReferenceId>(),
      referenceId: paramKey,
      referenceType: next.REFERENCED_TYPE.CONTEXT_PARAMETER,
      meaning: 'data source'
    } as next.ContextParameterFact,
    value: value
  } as next.BasicClause

  return [[clause]]
}

const upConditionWithBlocks = (
  conditionResult: boolean,
  condition: LogicCondition,
  sourceBlockId: next.BlockId,
  sourceBlock: Omit<next.Block, 'stepId' | 'orderIndex'>
): next.Clause[][] => {
  // CUSTOM_FUNCTION operator is returned when conditionOperator is not mapped
  const operator = upOperator(condition.conditionOperator, conditionResult)

  let value: next.ComparisonValue

  if (condition.conditionValue) {
    value = parseConditionValue(condition.conditionValue, operator, sourceBlock)
  } else if (condition.conditionValueFromPath) {
    // We need to get data from the source block
    // This is only possible when comma separated values are used for zip codes
    value = parseConditionValueFromBlockPath(
      condition.conditionValueFromPath,
      operator,
      sourceBlock
    )
  }

  let clause: next.Clause

  if (operator === next.LOGIC_OPERATOR.CUSTOM_FUNCTION) {
    if (!condition.conditionValueFromInternalIntegration) {
      throw new Error(
        `Expected 'conditionValueFromInternalIntegration' to be set`
      )
    }

    const functionName = parseLogicConditionValueFromInternalIntegration(
      condition.conditionValueFromInternalIntegration
    )

    if (!functionName) {
      throw new Error('Expected function name to be inferred')
    }

    clause = {
      operator: operator,
      fact: {
        id: newId<next.ReferenceId>(),
        referenceId: sourceBlockId,
        referenceType: next.REFERENCED_TYPE.BLOCK,
        meaning: 'data source'
      },
      value: undefined,
      functionName,
      args: value
        ? uncastObjectSchema.parse(value) // throws otherwise
        : undefined
    }
  } else {
    clause = {
      operator: operator,
      fact: {
        id: newId<next.ReferenceId>(),
        referenceId: sourceBlockId,
        referenceType: next.REFERENCED_TYPE.BLOCK,
        path: factPath(condition, sourceBlock.type),
        meaning: 'data source'
      },
      value: value
    }
  }

  return [[clause]]
}

const factPath = (
  condition: LogicCondition,
  controlName: next.BLOCK_TYPE
): string | undefined => {
  if (condition.liveValueFromPath) {
    switch (controlName) {
      case next.BLOCK_TYPE.PRODUCTS:
        return 'selected'

      default:
        return parseLogicLiveValueFromPath(condition.liveValueFromPath)
    }
  }

  switch (controlName) {
    case next.BLOCK_TYPE.NUMBER_INPUT:
      return 'numberInput'

    default:
      return
  }
}

const upOperator = (
  operator: LogicOperatorsValues | undefined,
  conditionResult: boolean
): next.LOGIC_OPERATOR => {
  const operatorMap = {
    [logicOperators.Equal]: next.LOGIC_OPERATOR.EQUAL,
    [logicOperators.Greater]: next.LOGIC_OPERATOR.GREATER_THAN,
    [logicOperators.GreaterOrEqual]: next.LOGIC_OPERATOR.GREATER_THAN_OR_EQUAL,
    [logicOperators.Less]: next.LOGIC_OPERATOR.LESS_THAN,
    [logicOperators.LessOrEqual]: next.LOGIC_OPERATOR.LESS_THAN_OR_EQUAL,
    [logicOperators.Between]: next.LOGIC_OPERATOR.BETWEEN,
    [logicOperators.NotEmpty]: next.LOGIC_OPERATOR.NOT_EMPTY,
    [logicOperators.IsEmpty]: next.LOGIC_OPERATOR.EMPTY,
    [logicOperators.ContEither]: next.LOGIC_OPERATOR.CONTAIN_EITHER,
    [logicOperators.ContAll]: next.LOGIC_OPERATOR.CONTAIN,
    [logicOperators.ContNot]: next.LOGIC_OPERATOR.NOT_CONTAIN,
    [logicOperators.ExactMatch]: next.LOGIC_OPERATOR.EQUAL,
    [logicOperators.In]: conditionResult
      ? next.LOGIC_OPERATOR.IN
      : next.LOGIC_OPERATOR.NOT_IN,
    [logicOperators.NotIn]: next.LOGIC_OPERATOR.NOT_IN
  }

  const result = operatorMap[operator as LogicOperatorsValues]

  if (!result) {
    // When the operator is not define the engine will need an external function to evaluate the logic
    return next.LOGIC_OPERATOR.CUSTOM_FUNCTION
  }

  return result
}

const parseConditionValue = (
  conditionValue: string | undefined,
  operator: next.LOGIC_OPERATOR,
  sourceBlock: Omit<next.Block, 'stepId' | 'orderIndex'>
): next.Clause['value'] => {
  if (!conditionValue) {
    throw new Error(`Condition value is required`)
  }

  const parsedConditionValue = parseLogicConditionValue(conditionValue)

  if (!parsedConditionValue) {
    throw new Error(`Could not parse condition value`)
  }

  return parseConditionValueForControl(
    parsedConditionValue,
    operator,
    sourceBlock
  )
}

const parseConditionValueFromBlockPath = (
  conditionValueFromPath: string,
  operator: next.LOGIC_OPERATOR,
  sourceBlock: Omit<next.Block, 'stepId' | 'orderIndex'>
) => {
  const path = parseLogicConditionValueFromPath(conditionValueFromPath)

  if (!path) {
    throw new Error(`Error while parsing conditionValueFromPath`)
  }

  // Condition value from path is only supported with Availability check
  if (
    sourceBlock.type === next.BLOCK_TYPE.AVAILABILITY_CHECK &&
    path === 'allowedPostalCodesCommaSeparated'
  ) {
    const blockSettings =
      sourceBlock.settings as next.AvailabilityCheckBlock['settings']

    return parseConditionValueForControl(
      blockSettings.allowedPostalCodesCommaSeparated || '',
      operator,
      sourceBlock
    )
  }
  throw new Error(
    `conditionValueFromPath ${path} is not supported by ${sourceBlock.type}`
  )
}

const parseConditionValueForControl = (
  value: string,
  operator: next.LOGIC_OPERATOR,
  sourceBlock: Omit<next.Block, 'stepId' | 'orderIndex'>
): next.Clause['value'] => {
  const controlName = sourceBlock.type

  switch (controlName) {
    // Single choice and Binary input
    case next.BLOCK_TYPE.BINARY_INPUT:
    case next.BLOCK_TYPE.SINGLE_CHOICE:
    case next.BLOCK_TYPE.PERSONAL_INFORMATION:
      if (['true', 'false'].includes(value)) {
        return value === 'true'
      }

      if (
        [next.LOGIC_OPERATOR.IN, next.LOGIC_OPERATOR.NOT_IN].includes(operator)
      ) {
        return value.split('///')
      }

      return value
    case next.BLOCK_TYPE.ENTITY_ATTRIBUTE: {
      const entityAttributeBlock = sourceBlock as next.EntityAttributeBlock

      const attributeType = entityAttributeBlock.settings.entityAttribute?.type

      switch (attributeType) {
        case 'boolean':
          return value === 'true'
        case 'select':
          if (operator === next.LOGIC_OPERATOR.EQUAL) {
            return value
          }

          return value.split('///')
        case 'multiselect':
          return value.split('///')
        default:
          return value
      }
    }
    case next.BLOCK_TYPE.MULTIPLE_CHOICE:
    case next.BLOCK_TYPE.PRODUCT_CATEGORIES:
      if (
        [
          next.LOGIC_OPERATOR.CONTAIN_EITHER,
          next.LOGIC_OPERATOR.CONTAIN,
          next.LOGIC_OPERATOR.NOT_CONTAIN,
          next.LOGIC_OPERATOR.EQUAL
        ].includes(operator)
      ) {
        return value.split('///')
      }
      throw new Error(
        `Invalid operator ${operator} for logic triggered by control ${controlName}`
      )
    case next.BLOCK_TYPE.PRODUCTS: {
      // Format -> product1|price1///product2|price2
      const productIDPriceIDStrings = value.split('///')
      const result: { productId: string; priceId: string }[] = []

      productIDPriceIDStrings.forEach((productIDPriceIDString) => {
        const [productID, priceID] = productIDPriceIDString.split('|')

        result.push({
          productId: productID,
          priceId: priceID
        })
      })

      return result
    }
    case next.BLOCK_TYPE.NUMBER_INPUT: {
      if (
        [
          next.LOGIC_OPERATOR.EQUAL,
          next.LOGIC_OPERATOR.GREATER_THAN,
          next.LOGIC_OPERATOR.GREATER_THAN_OR_EQUAL,
          next.LOGIC_OPERATOR.LESS_THAN,
          next.LOGIC_OPERATOR.LESS_THAN_OR_EQUAL
        ].includes(operator)
      )
        return Number(value)

      if (operator === next.LOGIC_OPERATOR.BETWEEN) {
        const [min, max] = value.split('///')

        return { min: Number(min), max: Number(max) }
      }
      throw new Error(
        `Invalid operator ${operator} for logic triggered by control ${controlName}`
      )
    }
    case next.BLOCK_TYPE.AVAILABILITY_CHECK: {
      if (operator === next.LOGIC_OPERATOR.CUSTOM_FUNCTION) {
        // The condition will only be a file, because the comma separated values are condition value path which is handled differently
        return { fileId: value }
      }
      if (operator === next.LOGIC_OPERATOR.NOT_IN) {
        return value.split(',')
      }
      throw new Error(
        `Unsupported operator ${operator} for control ${controlName}`
      )
    }
    default:
      return value
  }
}
